import { useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter507 · Client-side downscale before upload. Phone photos are
// 8–15 MB straight out of the camera; sending them raw across LTE
// costs 20-40s of wire time and is the #1 reason admins report
// "story picture takes forever to load / didn't save". This helper
// resizes to a 2048px longest edge and re-encodes to JPEG 82% —
// visually identical for a hero image, ~30–50× smaller on the wire.
// Falls back to the original file if anything about the canvas/blob
// pipeline throws so we never LOSE the upload.
export const shrinkImage = (file) =>
  new Promise((resolve) => {
    // Small files: don't bother touching them — the round-trip through
    // a canvas is more cost than the bytes we'd save.
    if (!file || !file.type.startsWith('image/') || file.size < 400 * 1024) {
      return resolve(file);
    }
    // Some browsers (Safari on iOS) can lose EXIF orientation via canvas.
    // The backend already bakes orientation server-side (`_bake_exif_orientation`),
    // so canvas here is safe.
    const MAX_EDGE = 2048;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { width: w, height: h } = img;
      const longest = Math.max(w, h);
      if (longest <= MAX_EDGE) {
        // Already small — keep the original bytes.
        URL.revokeObjectURL(url);
        return resolve(file);
      }
      const scale = MAX_EDGE / longest;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file); // graceful fallback
          const shrunk = new File([blob], file.name.replace(/\.(png|heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
          resolve(shrunk);
        },
        'image/jpeg',
        0.82,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

// Convert relative API URL to absolute for img tags.
// Passes through data: URLs and blob: URLs unchanged — those are already
// self-contained image sources (e.g. base64-encoded avatars stored on the
// user document by the /profile picture uploader). Without this passthrough
// they'd get concatenated onto BACKEND_URL and produce broken src tags,
// which is what caused the black placeholder on the Network page after
// changing a profile picture.
export const resolveImageUrl = (url, width) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  // iter440 · Cloudinary CDN fast-lane. When an asset already lives on
  // res.cloudinary.com we inject `f_auto,q_auto[,w_<width>]` right after
  // `/upload/` so every browser gets the best modern codec + quality
  // tier + resolution. Idempotent — we bail if a transformation slug
  // is already present (e.g. images baked via SDK preset).
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    const [prefix, tail] = url.split('/upload/');
    // If the very next path segment already contains a Cloudinary
    // transformation (letters + underscore), assume caller shaped it.
    const firstSeg = tail.split('/')[0] || '';
    if (/^[a-z]+_/.test(firstSeg)) {
      return url;
    }
    const parts = ['f_auto', 'q_auto'];
    if (width) parts.push(`w_${width}`);
    return `${prefix}/upload/${parts.join(',')}/${tail}`;
  }
  // iter305 · Defensive fix for legacy records that stored raw base64
  // WITHOUT the `data:image/...;base64,` prefix (spotted on the
  // /l/flythouse athlete during pre-review testing — cover/avatar
  // strings started with `QXrp...`, which is the base64 of `AZ...`,
  // so the <img> tag saw an unrecognized scheme and rendered a broken
  // icon). Detect it by (a) no scheme, (b) no path slash, (c) looks
  // like a base64 blob (base64 alphabet + length >= 128), and heal
  // by prepending the JPEG data-URL prefix in the browser.
  if (
    !url.startsWith('/') &&
    !url.includes('://') &&
    url.length >= 128 &&
    /^[A-Za-z0-9+/=]+$/.test(url.slice(0, 96))
  ) {
    return `data:image/jpeg;base64,${url}`;
  }
  const abs = (url.startsWith('http://') || url.startsWith('https://')) ? url : `${BACKEND_URL}${url}`;
  // iter346 · Native perf — always route uploaded assets through the
  // WebP fast-lane (/api/img) even when no explicit width is supplied.
  // On native iOS an unresized 10 MB PNG hero costs ~1.2s of wire time
  // and blocks the LCP; the same asset re-encoded to 800px WebP is
  // ~30 KB (350× smaller) and lands in ~130ms. Buckets: 400 for
  // avatars/thumbs, 1200 for hero/cover, 800 as a safe default.
  const OPT_HOST_RE = /(?:^|\.)(emergentagent\.(?:net|com)|flythouse\.com|espncdn\.com)$/i;
  const isUploadedPath = /\/api\/(?:files|uploads)\//i.test(abs);
  try {
    const u = new URL(abs);
    if (u.hostname === 'images.unsplash.com') {
      // Only rewrite unsplash URLs when a display width was requested —
      // some legacy tiles rely on the raw source rendering their default
      // dimensions, and appending `w=` to a URL that never had one 404s
      // for a subset of old assets.
      if (width) {
        u.searchParams.set('w', String(width));
        u.searchParams.set('q', '75');
        u.searchParams.set('auto', 'format');
      }
      return u.toString();
    }
    if (u.protocol === 'https:' && OPT_HOST_RE.test(u.hostname) && (width || isUploadedPath)) {
      const w2 = width || 1200;
      return `${BACKEND_URL}/api/img?src=${encodeURIComponent(abs)}&w=${w2}`;
    }
  } catch (_) { /* malformed URL — fall through untouched */ }
  return abs;
};

const ImageUpload = ({ value, onChange, onUploadingChange, label, aspectRatio = 'aspect-square', placeholder = 'Click to upload' }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('File too large (max 15MB)');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError('');
    onUploadingChange && onUploadingChange(true);

    try {
      // iter507 · Shrink client-side first — huge win on mobile Wi-Fi.
      const shrunk = await shrinkImage(file);
      const formData = new FormData();
      formData.append('file', shrunk);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(response.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      onUploadingChange && onUploadingChange(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleSelect = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const displayUrl = resolveImageUrl(value);

  return (
    <div>
      {label && <label className="font-body font-bold text-sm mb-2 block">{label}</label>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`${aspectRatio} border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition relative bg-gray-50 ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-black'
        }`}
      >
        {displayUrl && !uploading ? (
          <>
            <img loading="lazy" decoding="async" src={displayUrl} alt="Upload preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute top-2 right-2 bg-red-500 text-white text-xs w-7 h-7 rounded-full hover:bg-red-600 shadow-lg"
              title="Remove image"
            >×</button>
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
              Click to replace
            </div>
          </>
        ) : uploading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
            <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mb-2"></div>
            <p className="text-xs font-body text-gray-700 font-semibold">Uploading {progress > 0 ? `${progress}%` : ''}</p>
            {progress > 0 && (
              <div className="w-full max-w-[160px] h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-black transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-4 text-center">
            <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs font-body">{placeholder}</p>
            <p className="text-[10px] text-gray-400 mt-1">Drag & drop or click</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleSelect}
          className="hidden"
        />
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default ImageUpload;
